import React, { useEffect, useMemo, useState } from 'react'
import { STORAGE_KEYS } from '../config/apiConfig'
import { getFiles, getFacets } from '../services/datasetApi'
import Banner from '../components/Banner'
import SelectionToolbar from '../components/SelectionToolbar'
import Pagination from '../components/Pagination'
import FiltersPanel from '../components/FiltersPanel'
import ResultsTable from '../components/ResultsTable'
import Toast from '../components/Toast'
import Spinner from '../components/Spinner'

export default function Dataset() {
  const [apiKey, setApiKey] = useState(localStorage.getItem(STORAGE_KEYS.researcherKey) || '')
  const keyValid = useMemo(() => apiKey.trim().length > 0, [apiKey])
  const [showBanner, setShowBanner] = useState(false)

  const [facets, setFacets] = useState({})
  const [filters, setFilters] = useState({})
  const [rows, setRows] = useState([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(100)
  const [total, setTotal] = useState(0)
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState('')
  const [selected, setSelected] = useState(new Set())

  const queryFilters = useMemo(() => {
    const out = {}
    Object.entries(filters).forEach(([k, set]) => {
      if (set && set.size > 0) out[k] = Array.from(set)
    })
    return out
  }, [filters])

  const disabled = !keyValid

  const handleValidate = (e) => {
    e.preventDefault()
    if (!keyValid) {
      setShowBanner(true)
      return
    }
    localStorage.setItem(STORAGE_KEYS.researcherKey, apiKey.trim())
    setShowBanner(false)
    setPage(1)
    setSelected(new Set())
    // Files need the key; facets are public (already loaded on mount), but refreshing is fine:
    loadFacets()
    loadFiles(1, pageSize)
  }

  function toggleFacet(facet, value) {
    setFilters((prev) => {
      const next = { ...prev }
      const set = new Set(next[facet] || [])
      if (set.has(value)) set.delete(value)
      else set.add(value)
      next[facet] = set
      return next
    })
    setPage(1)
  }

  function toggleRow(key) {
    setSelected((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })
  }

  function copySelected() {
    const urls = rows
      .filter((r) => selected.has(r.rel_path || r.name))
      .map((r) => r.public_url)
      .filter(Boolean)
    const payload = urls.join('\n')
    if (!payload) {
      setToast('No URLs to copy.')
      return
    }
    navigator.clipboard.writeText(payload).then(
      () => setToast('Copied selected URLs'),
      () => setToast('Failed to copy')
    )
  }

  async function loadFacets() {
    try {
      const data = await getFacets({ /* path_prefix optional */ })
      // Backend sends { [facetKey]: { value: count } }. Keep raw;
      // FiltersPanel normalizes both shapes now.
      const f = data?.facets || data || {}
      setFacets(f)
    } catch {
      // facets are optional; do not block UI
    }
  }

  async function loadFiles(nextPage = page, limit = pageSize) {
    setBusy(true)
    try {
      const data = await getFiles({ page: nextPage, limit, filters: queryFilters })
      setRows(data?.files || [])
      setTotal(Number(data?.total || 0))
      setPage(Number(data?.page || nextPage))
    } catch (e) {
      setRows([])
      setTotal(0)
      setToast(e?.message || 'Failed to load files')
    } finally {
      setBusy(false)
    }
  }

  // NEW: always load facets on mount (public)
  useEffect(() => {
    loadFacets()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Reload when filters/pageSize change (files require key)
  useEffect(() => {
    if (!keyValid) return
    loadFiles(1, pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(queryFilters), pageSize, keyValid])

  // Reload when page changes
  useEffect(() => {
    if (!keyValid) return
    loadFiles(page, pageSize)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page])

  return (
    <section className="space-y-6">
      <h1 className="text-xl font-semibold">Dataset</h1>

      {showBanner && !keyValid && (
        <Banner
          type="warning"
          message={<span><strong>Invalid or missing researcher key.</strong> Please enter a valid key to continue.</span>}
          onDismiss={() => setShowBanner(false)}
        />
      )}

      <form onSubmit={handleValidate} className="flex flex-wrap items-end gap-2">
        <div className="grid gap-1">
          <label className="text-sm text-gray-700">Researcher Key</label>
          <input
            className="h-10 w-80 min-w-[260px] rounded border px-3 outline-none focus:ring-2 focus:ring-gray-400"
            placeholder="Enter your key…"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            autoComplete="off"
            spellCheck={false}
          />
        </div>
        <button
          type="submit"
          className="h-10 rounded bg-gray-900 px-4 text-white hover:bg-black"
        >
          Continue
        </button>
      </form>

      <SelectionToolbar
        selectedCount={selected.size}
        onCopy={copySelected}
        disabled={disabled}
      />

      <div className="grid grid-cols-1 gap-6 md:grid-cols-4">
        <aside className="md:col-span-1">
          <FiltersPanel
            facets={facets}
            selected={filters}
            onToggle={toggleFacet}
            disabled={disabled}
          />
        </aside>

        <section className="md:col-span-3 space-y-2">
          {busy ? (
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Spinner size={5} /> Loading…
            </div>
          ) : null}

          <ResultsTable
            rows={rows}
            selected={selected}
            onToggleRow={toggleRow}
            pageSize={pageSize}
            onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
            disabled={disabled}
          />

          <Pagination
            page={page}
            totalPages={totalPages}
            onPageChange={(p) => setPage(Math.max(1, Math.min(totalPages, p)))}
            disabled={disabled || busy}
          />
        </section>
      </div>

      <Toast message={toast} onClose={() => setToast('')} />
    </section>
  )
}
